import {
  AfterViewInit,
  Component,
  ElementRef,
  forwardRef,
  input,
  OnDestroy,
  viewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { EditorView, basicSetup } from 'codemirror';
import { EditorState } from '@codemirror/state';
import { html } from '@codemirror/lang-html';

/**
 * Reusable HTML source code editor based on CodeMirror 6.
 *
 * Implements {@link ControlValueAccessor} so it can be used with reactive forms
 * (`[formControl]` / `formControlName`). Edits raw HTML/FreeMarker source
 * (`${...}`, `<#if>`) losslessly, with syntax highlighting and line numbers.
 */
@Component({
  selector: 'app-html-code-editor',
  standalone: true,
  templateUrl: './html-code-editor.component.html',
  styleUrl: './html-code-editor.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => HtmlCodeEditorComponent),
      multi: true
    }
  ]
})
export class HtmlCodeEditorComponent implements AfterViewInit, OnDestroy, ControlValueAccessor {
  label = input<string>('');

  private hostRef = viewChild.required<ElementRef<HTMLDivElement>>('host');
  private view?: EditorView;

  /** Buffered value written before the view is initialized. */
  private pendingValue = '';
  private disabled = false;

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  ngAfterViewInit(): void {
    this.view = new EditorView({
      parent: this.hostRef().nativeElement,
      state: EditorState.create({
        doc: this.pendingValue,
        extensions: [
          basicSetup,
          html(),
          EditorView.lineWrapping,
          EditorView.editable.of(!this.disabled),
          EditorView.updateListener.of((update) => {
            if (update.docChanged) {
              this.onChange(update.state.doc.toString());
            }
            if (update.focusChanged && !update.view.hasFocus) {
              this.onTouched();
            }
          })
        ]
      })
    });
  }

  ngOnDestroy(): void {
    this.view?.destroy();
  }

  writeValue(value: string | null): void {
    const next = value ?? '';
    if (!this.view) {
      this.pendingValue = next;
      return;
    }
    const current = this.view.state.doc.toString();
    if (current !== next) {
      this.view.dispatch({
        changes: { from: 0, to: current.length, insert: next }
      });
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    // Toggle editability on the live editor's content node.
    this.view?.contentDOM.setAttribute('contenteditable', String(!isDisabled));
  }
}
